import * as React from 'react';
import { connect } from 'react-redux';
import { AppState } from '../../store';
import { bindActionCreators, Dispatch } from 'redux';
import * as ReportStore from '../../store/Report';
import {  Badge, Col, OverlayTrigger, Row, Spinner, Tooltip } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import { Report } from '../../types/Report/Report';
import { history } from '../../store/configureStore';
import { User } from 'oidc-client';
import ReactTimeAgo from 'react-time-ago';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faExclamationTriangle, faLockAlt, faUnlockAlt } from '@fortawesome/pro-solid-svg-icons';
import NoPermissions from '../Common/NoPermissions/NoPermissions';
import { DatasetRefreshStatusType } from '../../types/Report/Enums/DatasetRefreshStatusType';

type ReportTableProps = {
    dispatch: Dispatch;
    reportState: ReportStore.ReportState;
    user: User;
    actions:  typeof ReportStore.actionCreators;
}

interface ReportTableState {
    report: Report | undefined;
}

class ReportsTable extends React.Component<ReportTableProps, ReportTableState> {
    public constructor(props: ReportTableProps) {
        super(props);
        this.state = {
            report: undefined,
        };
    }

    public componentDidMount() {
        this.ensureDataFetched();
    }

    public componentDidUpdate() {
        this.ensureDataFetched();
    }

    private ensureDataFetched() {
        this.props.actions.requestReports();
    }


    render() {
        const NoDataComponent: React.FunctionComponent = (): React.ReactElement => {
            if(this.props.reportState.isLoadedData){
                return (
                    <div className="d-flex justify-content-center mt-3" data-cy="bootstrap-table-loaded">
                        <p className="text-center">No reports were found.</p>
                    </div>
                );
            }
            else{
                return (
                    <div className="d-flex justify-content-center mt-3">
                        <Spinner animation="border" variant="primary" role="status">
                            <span className="sr-only">Loading...</span>
                        </Spinner>
                    </div>
                );
            }
        };

        if (!this.props.reportState.hasPermissionsToList) {
            return (
                <NoPermissions />
            );
        }

        const { SearchBar } = Search;

        const columns = [
            {
                dataField: 'name',
                text: 'Name',
                sort: true,
            },
            {
                dataField: 'id',
                text: 'Report ID',
                sort: true,
            },
            {
                dataField: 'groupName',
                text: 'Group',
                sort: true,
            },
            {
                dataField: 'lastDatasetRefreshHistoryDate',
                text: 'Refresh Status',
                sort: true,
                formatter: (cell: string, row: Report) => {
                    return <React.Fragment>
                        { 
                        row.lastDatasetRefreshHistoryStatus === DatasetRefreshStatusType.Completed ? <><Badge bg='success'>Completed { row.isRefreshScheduleEnabled ? <FontAwesomeIcon icon={faClock} /> : "" }</Badge><br /></>
                        : row.lastDatasetRefreshHistoryStatus === DatasetRefreshStatusType.Failed ? <><Badge bg='danger'>Failed { row.isRefreshScheduleEnabled ? <FontAwesomeIcon icon={faClock} /> : "" }</Badge><br /></>
                        : row.lastDatasetRefreshHistoryStatus === DatasetRefreshStatusType.Disabled ? <><Badge bg='secondary'>Disabled { row.isRefreshScheduleEnabled ? <FontAwesomeIcon icon={faClock} /> : "" }</Badge><br /></>
                        : ""
                        }
                        <span className={row.isStale ? "text-danger" : ""}>
                        { row.isStale ? <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '4px' }} /> : "" } 
                        { cell ? <ReactTimeAgo date={new Date(cell)} locale="en-US"/> : "" }
                        </span>
                    </React.Fragment>
                },
            },
            {
                dataField: 'isStale',
                text: 'Is Stale?',
                sort: true,
                formatter: (cell: boolean, row: Report) => {
                    return row.isStale ? "Yes" : "";
                },
            },
            {
                dataField: 'isAccessControlEntryRequired',
                text: 'Authorization',
                sort: true,
                formatter: (cell: string, row: Report) => {
                    return row.isAccessControlEntryRequired ? 
                        <OverlayTrigger
                         placement='bottom'
                         overlay={
                            <Tooltip>Report has access controls applied.</Tooltip>
                         }
                        >
                        <span style={{"color": "darkgreen"}}><FontAwesomeIcon icon={faLockAlt}/></span>
                        </OverlayTrigger>
                         : 
                        <OverlayTrigger
                         placement='bottom'
                         overlay={
                            <Tooltip>Report is visible to everyone.</Tooltip>
                         }
                        >
                        <span style={{"color": "darkred"}}><FontAwesomeIcon icon={faUnlockAlt}/></span>
                        </OverlayTrigger>
                        
                }

            },
            {
                dataField: 'tenantDomain',
                text: 'Tenant',
                sort: true,
            }
        ];

        const rowEvents: Partial<any> = {
            onClick: (e: React.MouseEvent<HTMLButtonElement>, row: Report, rowIndex: number) => {
                e.preventDefault();
                e.stopPropagation();
                let target = e.target as HTMLButtonElement;
                // Only goto tenant details when it's not a link click
                if(!target.classList.contains('btn-link')){
                    history.push(`/reports/${row.id}`);
                }
            },
        };
        
        return (
            <React.Fragment>
                <ToolkitProvider
                    keyField="id"
                    search
                    data={ this.props.reportState.data } 
                    columns={ columns } 
                    >
                    {
                        props => (
                        <React.Fragment>
                            <Row>
                                <Col>
                                    <SearchBar { ...props.searchProps } />
                                </Col>
                            </Row>
                            <BootstrapTable
                                { ...props.baseProps }
                                id="reportTable"
                                classes='clickable'
                                striped
                                hover
                                bordered={false}
                                defaultSorted={ [{ dataField: 'name', order: 'asc' }] }
                                pagination={ paginationFactory({sizePerPage: 25}) }
                                noDataIndication={ () => <NoDataComponent /> }
                                rowEvents={ rowEvents }
                            />
                        </React.Fragment>
                        )
                    }
                </ToolkitProvider>

            </React.Fragment>
        );
    }
}

function mapStateToProps(state: AppState) {
    return {
        reportState: state.report,
        user: state.oidc.user,
    };
}

export default connect(
    mapStateToProps,
    (dispatch) => {
        return {
            dispatch: dispatch,
            actions: bindActionCreators(Object.assign(
                {}, 
                ReportStore.actionCreators,
            ), dispatch )
        }
    }
)(ReportsTable as any);